import { useEffect, useState } from "react";
import { useStore } from "ra-core";

import { ThemeProviderContext, type Theme } from "./theme-context";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

/**
 * Theme provider that enables light, dark, and system theme modes.
 * Persists theme selection across reloads via localStorage & ra-core store.
 */
export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "theme",
  ...props
}: ThemeProviderProps) {
  const [storeTheme, setStoreTheme] = useStore<Theme>(storageKey, defaultTheme);

  const [themeState, setThemeState] = useState<Theme>(() => {
    const saved = localStorage.getItem(storageKey) as Theme | null;
    return saved || storeTheme || defaultTheme;
  });

  const setTheme = (newTheme: Theme) => {
    localStorage.setItem(storageKey, newTheme);
    setThemeState(newTheme);
    setStoreTheme(newTheme);
  };

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");

    const activeTheme =
      themeState === "system"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light"
        : themeState;

    root.classList.add(activeTheme);
  }, [themeState]);

  const value = {
    theme: themeState,
    setTheme,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}
